import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, CheckCircle, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

/**
 * CheckEmail Page - Shown after signup
 * Prompts user to verify their email with resend functionality
 */
const CheckEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || 'your email';
  
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleResendEmail = async () => {
    if (countdown > 0 || resending) return;

    setResending(true);
    setResendSuccess(false);

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) {
        throw error;
      }

      setResendSuccess(true);
      setCountdown(60); // 60 second cooldown
      toast.success('Verification email sent! Check your inbox.');
    } catch (error) {
      console.error('Error resending verification email:', error);
      toast.error(error.message || 'Failed to resend email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen-safe flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4">
      <Card className="max-w-md w-full shadow-xl">
        <CardContent className="pt-12 pb-8 px-8">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="bg-primary/10 p-4 rounded-full">
              <Mail className="h-12 w-12 text-primary" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-heading font-bold text-center mb-3">
            Check your email
          </h1>

          {/* Description */}
          <p className="text-center text-muted-foreground mb-6">
            We sent a verification link to:
          </p>
          
          {/* Email Display */}
          <div className="bg-gray-100 rounded-lg p-3 mb-6 text-center">
            <p className="font-semibold text-gray-900 break-all">{email}</p>
          </div>

          {/* Instructions */}
          <div className="space-y-3 mb-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">
                Click the verification link in the email to activate your account
              </p>
            </div>
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">
                <strong>Can't find the email?</strong> Check your spam or junk folder
              </p>
            </div>
          </div>

          {/* Resend Success Message */}
          {resendSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-800">
                Verification email sent successfully!
              </p>
            </div>
          )}

          {/* Resend Button */}
          <Button
            onClick={handleResendEmail}
            disabled={countdown > 0 || resending}
            variant={countdown > 0 ? "outline" : "default"}
            className="w-full rounded-xl mb-4"
          >
            {resending ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : countdown > 0 ? (
              `Resend available in ${countdown}s`
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Resend verification email
              </>
            )}
          </Button>

          {/* Back to Login */}
          <div className="text-center">
            <Button
              variant="link"
              onClick={() => navigate('/auth')}
              className="text-sm text-primary hover:underline"
            >
              Back to login
            </Button>
          </div>

          {/* Help Text */}
          <div className="mt-6 pt-6 border-t text-center">
            <p className="text-xs text-muted-foreground">
              Still having trouble?{' '}
              <a 
                href="mailto:support@boholtrabaho.app" 
                className="text-primary hover:underline"
              >
                Contact support
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CheckEmail;
